import { mapActions } from 'vuex';

import tabSessionManager from '@/utilities/tabSessionManager';
import CartConflictAlert from '@/components/modals/CartConflictAlert.vue';

/**
 * Mixin that synchronizes tab state across browser tabs.
 *
 * Responsibilities:
 * - Prevent multiple active cart sessions across tabs
 * - React to cart opening events from other tabs
 * - React to customer context changes across tabs
 */
export default {
  components: { CartConflictAlert },

  data() {
    return {
      // cart session
      showSingleCartAlert: false,
      cartUnsubscriber: null,
      tabId: null,

      // customer session
      showCustomerChangedAlert: false,
      customerUnsubscriber: null,
    };
  },

  /**
   * Subscribes to cross-tab events and notifies other tabs that a cart session was opened.
   */
  async mounted() {
    this.tabId = tabSessionManager.generateTabId();
    const customerKey = await this.getCustomerCodeStorageKey();

    this.cartUnsubscriber = tabSessionManager.onCartOpened(
      this.handleCartOpened
    );
    this.customerUnsubscriber = tabSessionManager.onCustomerChanged(
      customerKey,
      this.handleCustomerChanged
    );

    tabSessionManager.notifyCartOpened(this.tabId);
  },

  /**
   * Unsubscribes from all cross-tab listeners to avoid memory leaks.
   */
  beforeDestroy() {
    this.cartUnsubscriber?.();
    this.customerUnsubscriber?.();
  },

  methods: {
    ...mapActions('selectedUser', ['getCustomerCodeStorageKey']),

    /**
     * Handles cart session events coming from other browser tabs.
     * Ignores events originated from the current tab.
     *
     * @param {string} incomingTabId - Identifier of the tab that triggered the event
     */
    handleCartOpened(incomingTabId) {
      if (incomingTabId === this.tabId) return;
      this.showSingleCartAlert = true;
    },

    /**
     * Handles customer context changes coming from other tabs.
     * Prevents reacting to no-op updates (same value).
     *
     * @param {string|null} newValue - New customer value
     * @param {string|null} oldValue - Previous customer value
     */
    handleCustomerChanged(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.showCustomerChangedAlert = true;
    },
  },
};
